import { TrackingEvent } from "./trackingEvent"
import { mobile, tablet } from "./types/breakpoint.constant"
import { EventInterface } from "./types/event.interface"
import { ThemeComponentsInterface } from "./types/themeComponents.interface"
import { ThemeConfig } from "./types/themeConfig.interface"
import { ThemeHandlerInterface } from "./types/themeHandler.interface"
import { ThemeServiceInterface } from "./types/themeService.interface"

export class ThemeHandler implements ThemeHandlerInterface {
    private service: ThemeServiceInterface
    private components: ThemeComponentsInterface
    private configs: ThemeConfig = {} as ThemeConfig
    private trackingEvent: TrackingEvent

    constructor(service: ThemeServiceInterface, components) {
        this.service = service
        this.components = components
        this.trackingEvent = new TrackingEvent()
    }

    private async setConfigs(): Promise<void> {
        this.configs = await this.service.getThemeSettings()
    }

    private setLoading(force?: boolean): void {
        this.components.loader.classList.toggle('hidden', !force)
    }

    private setBrandName(): void {
        if (!this.configs.name) return
        this.components.brandName.textContent = this.configs.name
    }

    private setLogo(): void {
        const { imageUrl } = this.configs.logos?.find(({ type }) => type.toLowerCase() === this.configs.mode?.toLowerCase()) || {}
    
        if (!imageUrl) {
            this.setBrandName()
            return
        }

        this.components.brandLogo.src = imageUrl
        this.components.brandLogo.classList.remove('hidden')
        this.components.brandName.classList.add('hidden')
    }

    private defineBackgroundSize(): void {
        const screenWidth = window.innerWidth
        const backgroundImage = this.configs.backgroundImage

        if (screenWidth <= mobile) {
            this.components.brandBackground.style.backgroundImage = `url('${backgroundImage?.mobile}')`
            return
        }

        if (screenWidth <= tablet) {
            this.components.brandBackground.style.backgroundImage = `url('${backgroundImage?.tablet}')`
            return
        }

        this.components.brandBackground.style.backgroundImage = `url('${backgroundImage?.desktop}')`
    }

    private setBackground(): void {
        if (!this.configs.backgroundImage) return

        this.components.brandBackground.classList.remove('default')
        this.defineBackgroundSize()
    }

    private setMode(): void { 
        if (!this.configs.mode) return

        this.components.root.classList.remove(`${this.service.defaultMode}-mode`)
        this.components.root.classList.add(`${this.configs.mode}-mode`)
    }

    async apply() {
        try {
            this.setLoading(true)
            await this.setConfigs()

            this.setMode()
            this.setLogo()
            this.setBackground()
        } catch (error: any) {
            if(global.ENV === 'staging') {
                console.error(error)
            }

            const event: EventInterface = {
                brand: this.service.brand,
                error: {
                    message: error?.message || '',
                    path: window.location.href,
                    code: 'SSO_RUNTIME'                
                }
            }
            this.trackingEvent.send(event)
            return
        } finally {
            this.setLoading(false)
        }
    }
}

